import { BASE_URL } from "../config/api";
import { getAccessToken, getIdToken } from "./authSessionService";
import { clearSessionAndRedirectToLogin } from "./authService";

export class ApiError extends Error {
  readonly status: number;
  /** Stable `code` from API error JSON when present (e.g. BU_REMOVAL_CONFIRMATION_REQUIRED). */
  readonly apiCode?: string;
  readonly context?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    apiCode?: string,
    context?: Record<string, unknown>
  ) {
    super(message);
    this.status = status;
    this.apiCode = apiCode;
    this.context = context;
  }
}

type ApiRequestOptions = {
  readonly requiresAuth?: boolean;
  readonly redirectOnUnauthorized?: boolean;
};

function toApiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (path.startsWith("/")) {
    return `${BASE_URL}${path}`;
  }
  return `${BASE_URL}/${path}`;
}

async function parseApiError(response: Response): Promise<{
  message: string;
  apiCode?: string;
  context?: Record<string, unknown>;
}> {
  const raw = await response.text();
  if (!raw) {
    return { message: `Request failed (${response.status})` };
  }
  try {
    const parsed = JSON.parse(raw) as {
      detail?: unknown;
      message?: string;
      code?: string;
      context?: Record<string, unknown> | null;
    };
    const detailStr =
      typeof parsed.detail === "string" ? parsed.detail : undefined;
    const message = detailStr ?? parsed.message ?? raw;
    const apiCode = typeof parsed.code === "string" ? parsed.code : undefined;
    const context =
      parsed.context !== null &&
      parsed.context !== undefined &&
      typeof parsed.context === "object"
        ? parsed.context
        : undefined;
    return { message, apiCode, context };
  } catch {
    return { message: raw };
  }
}

function handleUnauthorizedRedirect(): void {
  clearSessionAndRedirectToLogin();
}

export async function apiRequest(
  path: string,
  init?: RequestInit,
  options?: ApiRequestOptions
): Promise<Response> {
  const requiresAuth = options?.requiresAuth ?? true;
  const redirectOnUnauthorized = options?.redirectOnUnauthorized ?? true;
  const headers = new Headers(init?.headers ?? {});

  if (requiresAuth) {
    const token = getAccessToken() ?? getIdToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  const response = await fetch(toApiUrl(path), {
    ...init,
    headers,
  });

  if (response.status === 401 && redirectOnUnauthorized && requiresAuth) {
    handleUnauthorizedRedirect();
  }

  if (!response.ok) {
    const parsed = await parseApiError(response);
    throw new ApiError(
      parsed.message,
      response.status,
      parsed.apiCode,
      parsed.context
    );
  }

  return response;
}
